// Wave 3b (2026-11-04): the guided tour on the web. The api (api/src/tour.ts) keeps the step and moves it on as the
// tour's video gets each part (a sheet started, a place drawn, a voice picked, a line written, the scene made); this
// file is the pure side: each step's words, where its highlight points, the step indicator, the free-minutes clock
// and when the credit packs should open. Unit-tested in tour.test.ts; drawn by components/Tour.tsx.

export const TOUR_STEPS = ["welcome", "character", "place", "voice", "line", "make", "done"] as const;
export type TourStep = (typeof TOUR_STEPS)[number];
export const isTourStep = (s: unknown): s is TourStep => typeof s === "string" && (TOUR_STEPS as readonly string[]).includes(s);

/** GET /api/tour. */
export type TourState = {
  step: TourStep;
  active: boolean;
  offer: boolean;
  startedAt: string | null;
  completedAt: string | null;
  dismissedAt: string | null;
  projectId: string | null;
  freeSeconds: number;
  freeSecondsLeft: number;
  estimateCredits: number;
  sheetCredits: number;
};

export const TOUR_FREE_SECONDS = 180;

const num = (v: unknown, d = 0) => (typeof v === "number" && Number.isFinite(v) ? v : d);
const strOrNull = (v: unknown) => (typeof v === "string" && v ? v : null);

/** A tolerant read of the api's answer (an older api, or none, is a tour that isn't on). */
export function normTour(o: unknown): TourState {
  const x = (o && typeof o === "object" ? o : {}) as Record<string, unknown>;
  return {
    step: isTourStep(x.step) ? x.step : "welcome",
    active: x.active === true,
    offer: x.offer === true,
    startedAt: strOrNull(x.startedAt),
    completedAt: strOrNull(x.completedAt),
    dismissedAt: strOrNull(x.dismissedAt),
    projectId: strOrNull(x.projectId),
    freeSeconds: num(x.freeSeconds, TOUR_FREE_SECONDS),
    freeSecondsLeft: Math.max(0, num(x.freeSecondsLeft)),
    estimateCredits: num(x.estimateCredits, 150),
    sheetCredits: num(x.sheetCredits, 45),
  };
}

export type StepCopy = {
  title: string;
  /** what to do, by voice or by tap */
  body: string;
  /** what to say to the Director, as a suggestion chip */
  say: string;
  /** where the highlight points, in order (the first visible one wins); CSS selectors of stable controls */
  targets: string[];
  /** the screen the step happens on */
  on: "home" | "project";
};

/** The steps the indicator counts (welcome and done sit outside it). */
export const COUNTED: TourStep[] = ["character", "place", "voice", "line", "make"];

export function stepCopy(step: TourStep, t: Pick<TourState, "sheetCredits" | "estimateCredits"> = { sheetCredits: 45, estimateCredits: 150 }): StepCopy {
  switch (step) {
    case "welcome":
      return { title: "Make your first scene", body: `Describe a character and a place, pick a voice, and they say your line. About ${t.estimateCredits} credits for the whole scene, and nothing costs anything until the character is drawn.`, say: "Let's make my first scene", targets: ['[data-action="call.talk"]'], on: "home" };
    case "character":
      return { title: "Who's in it?", body: "Tell your Director who the scene is about and what they look like, or tap Add. Drawing their character sheet is the first paid step.", say: "It's about a little owl called Pip", targets: ['[data-action="character.sheet"]', '[data-action="cast.add"]', '[data-action="call.talk"]'], on: "project" };
    case "place":
      return { title: "Where are they?", body: "Describe one place in a sentence: a lighthouse at dusk, a messy kitchen. About 15 credits to draw it.", say: "They're in a lighthouse at dusk", targets: ['[data-action="world.new"]', '#world-tab-location[aria-selected="false"]', '[data-action="call.talk"]'], on: "project" };
    case "voice":
      return { title: "Pick their voice", body: "Hear a few voices and keep the one that fits. Listening is free.", say: "Let me hear a few voices for them", targets: ['[data-action="cast.voice"]', '#world-tab-characters[aria-selected="false"]', '[data-action="call.talk"]'], on: "project" };
    case "line":
      return { title: "What do they say?", body: "One short line, the way they'd say it.", say: "They say: I can see the whole sea from up here", targets: ['[data-action="scene.edit"]', '[data-action="scene.add"]', '[data-action="call.talk"]'], on: "project" };
    case "make":
      return { title: "Make the scene", body: "You'll hear the price first. Then the picture, the voice and a little movement come together.", say: "Make the scene", targets: ['[data-action="produce.go"]', '[data-action="project.next-step"]', '[data-action="render.go"]', ".render-btn", '[data-action="call.talk"]'], on: "project" };
    case "done":
      return { title: "Your first scene", body: "It plays here when it's ready. Want to add a second scene?", say: "Let's add a second scene", targets: ['[data-action="scene.add"]'], on: "project" };
  }
}

/** "Step 2 of 5" (null outside the counted steps). */
export function stepNumber(step: TourStep): { n: number; of: number } | null {
  const i = COUNTED.indexOf(step);
  return i < 0 ? null : { n: i + 1, of: COUNTED.length };
}

/** The step after this one (Skip this step). */
export function nextStepOf(step: TourStep): TourStep {
  const i = TOUR_STEPS.indexOf(step);
  return TOUR_STEPS[Math.min(TOUR_STEPS.length - 1, i + 1)]!;
}

/** The character step's price line, and whether the packs should be offered (the balance is short of the sheet). */
export function sheetPriceLine(balance: number | null, sheetCredits: number): { text: string; short: boolean } {
  const short = balance !== null && balance < sheetCredits;
  if (short) return { text: `The sheet is about ${sheetCredits} credits and you have ${Math.max(0, balance!)}. Pick a credit pack to draw it; everything up to here was free.`, short: true };
  return { text: `The sheet is about ${sheetCredits} credits. Everything up to here was free.`, short: false };
}

/** "2:31" of free Director time. */
export function freeClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * The free tour minutes left during a call: the call's own free seconds less the time it has run (null when the call
 * has none, or they're used up).
 */
export function freeLeftDuringCall(freeSeconds: number | null | undefined, elapsedS: number): number | null {
  if (!freeSeconds || freeSeconds <= 0) return null;
  const left = freeSeconds - Math.max(0, elapsedS);
  return left > 0 ? left : null;
}

/**
 * Whether the call's end is where its free tour minutes run out (the balance adds less than a minute past them), so
 * the wrap-up and the end say the free minutes are used up and the packs open.
 */
export function freeTimeEnding(freeSeconds: number | null | undefined, limitS: number | null | undefined): boolean {
  if (!freeSeconds || freeSeconds <= 0 || !limitS || limitS <= 0) return false;
  return limitS - freeSeconds < 60;
}

/** Where the highlight should sit on screen (the target's box, padded, kept inside the viewport). */
export function ringBox(r: { top: number; left: number; width: number; height: number }, vw: number, vh: number, pad = 8): { top: number; left: number; width: number; height: number } {
  const top = Math.max(4, r.top - pad);
  const left = Math.max(4, r.left - pad);
  const right = Math.min(vw - 4, r.left + r.width + pad);
  const bottom = Math.min(vh - 4, r.top + r.height + pad);
  return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

/** Whether the tour's card shows on this path (its steps happen on Home and on the tour's video). */
export function showsOn(path: string, step: TourStep, projectId: string | null): boolean {
  if (/^\/(credits|settings|privacy|terms|s\/)/.test(path)) return false;
  if (stepCopy(step).on === "home") return true;
  return path === "/" || (!!projectId && path.startsWith(`/p/${projectId}`));
}
